import { useIsMounted, usePrevious } from "@react-hookz/web";
import { RefCallback, useCallback, useEffect, useRef, useState } from "react";
import { useLiveRef } from "../useLiveRef/useLiveRef";

export interface UseIsScrollingOptions {
    checkDelay?: number;
}

export interface UseIsScrollingResult<ElementType extends HTMLElement = HTMLDivElement> {
    isScrolling: boolean;
    ref: RefCallback<ElementType>;
}

/**
 * Provides a ref to attach to an element. Will also return `isScrolling` = true if the element
 * has scrolled within the last `checkDelay` milliseconds.
 *
 * This was loosely based on useScrolling from https://github.com/streamich/react-use but has been
 * optimized to cut out a few extra renders.
 */
export const useIsScrolling = <ElementType extends HTMLElement = HTMLDivElement>({
    checkDelay = 150,
}: UseIsScrollingOptions = {}): UseIsScrollingResult<ElementType> => {
    const [isScrolling, setIsScrolling] = useState<boolean>(false);
    const latestIsScrollingRef = useLiveRef(isScrolling);

    const elementRef = useRef<ElementType | null>(null);
    const isMounted = useIsMounted();

    // Tracks the latest timeout id.
    const latestTimerRef = useRef(0);

    const handleScrollEnd = useCallback(() => {
        // Avoid re-renders that don't do anything...
        if (isMounted() && latestIsScrollingRef.current) {
            setIsScrolling(false);
        }
    }, [isMounted, latestIsScrollingRef]);

    const handleScroll = useCallback(() => {
        // This check avoids re-renders on React 16 which doesn't no-op state setters that are
        // equivalent.
        if (!latestIsScrollingRef.current) {
            setIsScrolling(true);
        }
        clearTimeout(latestTimerRef.current);
        latestTimerRef.current = window.setTimeout(handleScrollEnd, checkDelay);
    }, [checkDelay, handleScrollEnd, latestIsScrollingRef]);

    const removeScrollHandler = useCallback(
        (element: ElementType) => {
            element.removeEventListener("scroll", handleScroll);
            setIsScrolling(false);
        },
        [handleScroll]
    );

    const attachScrollHandler = useCallback(
        (element: ElementType) => {
            element.addEventListener("scroll", handleScroll, {
                passive: true,
            });
        },
        [handleScroll]
    );

    const previousCheckDelay = usePrevious(checkDelay);
    useEffect(() => {
        // If our delay changed and we're in the middle of scrolling, restart the timer w/ the new
        // delay.
        if (previousCheckDelay && checkDelay !== previousCheckDelay && latestIsScrollingRef.current) {
            handleScroll();
        }
    }, [checkDelay, handleScroll, latestIsScrollingRef, previousCheckDelay]);

    return {
        isScrolling,
        ref: useCallback(
            (element: ElementType) => {
                if (element) {
                    elementRef.current = element;
                    attachScrollHandler(element);
                } else if (elementRef.current) {
                    // This means the hook is unmounting.
                    removeScrollHandler(elementRef.current);
                    handleScrollEnd();
                    clearTimeout(latestTimerRef.current);
                }
            },
            [attachScrollHandler, handleScrollEnd, removeScrollHandler]
        ),
    };
};
